import * as React from 'react';
import {DraftAccount, RegistrationRequirement} from '../data/types';
import {routes} from '../routes';

/**
 * Which screens contain which requirements
 */
const routesToRequirements: {
  [x: string]: RegistrationRequirement[];
} = {
  [routes.TellUsMore]: ['why', 'relationship', 'questions_or_comments'],
  [routes.Name]: ['firstname', 'middlename', 'lastname'],
  [routes.Birthday]: ['birthdate'],
  [routes.PhoneNumber]: ['phone', 'responsible_phone'],
  [routes.Email]: ['email', 'responsible_email'],
  [routes.ConfirmEmail]: [
    'email_verification',
    'responsible_email_verification',
  ],
  // No requirements, just confirmation page
  [routes.EmailConfirmed]: [],
  [routes.Username]: ['username'],
  [routes.Password]: ['password'],
};

/**
 * Which screens contain requirements (determine default order)
 */
const requirementScreens = [
  routes.TellUsMore,
  routes.Name,
  routes.Birthday,
  routes.PhoneNumber,
  routes.Email,
  routes.ConfirmEmail,
  routes.EmailConfirmed,
  routes.Username,
  routes.Password,
];

/**
 * Screens that all users must go through, but not containing requirements
 */
const nonRequirementScreens = [
  routes.LinkPractice,
  routes.RegistrationMethods,
  routes.Success,
];

/**
 * These are dynamic requirements that for some reason we are forced
 * to display before the non-dynamic screens.
 */
const preRegistrationMethodRequirementScreens = [routes.TellUsMore];

export function remainingPreRegistrationMethodRequirements(
  remainingRequirements: string[]
): string[] {
  return preRegistrationMethodRequirementScreens
    .flatMap(route => routesToRequirements[route])
    .filter(req => remainingRequirements.includes(req));
}

/**
 * Get the next logical screen considering current route and state
 */
export function nextRequirementScreen({
  location,
  registrationCompleted,
  remainingRequirements,
  draftAccount,
}: {
  location: {
    pathname: string;
  };
  registrationCompleted: boolean;
  remainingRequirements: Array<string>;
  draftAccount: DraftAccount;
}): string {
  const {pathname} = location;
  // Username screen only required for children.  This is a temporarily quasi-permanent
  // fix, we should actually be able to assume that the backend correctly tells us
  // the requirements but we know that it doesn't here.
  const ageBasedScreens = draftAccount.useResponsibleEmail
    ? requirementScreens
    : (requirementScreens.filter(
        screen => screen !== routes.Username
      ) as string[]);
  const nextInOrder = ageBasedScreens[ageBasedScreens.indexOf(pathname) + 1];

  if (
    registrationCompleted &&
    (!nextInOrder || remainingRequirements.length === 0)
  ) {
    // We're at the end. Go to success screen.
    return routes.Success;
  }

  if (registrationCompleted || remainingRequirements.length === 0) {
    // Allow navigating through the regular flow
    return nextInOrder;
  }

  // Find the next logical screen with remaining requirements
  return (
    ageBasedScreens.find(routeName =>
      routesToRequirements[routeName].some(requirement =>
        remainingRequirements.includes(requirement)
      )
    ) || nextInOrder
  );
}

/**
 * Calculate progress 0 -> 1 based on current route and state of draftAccount
 */
export function calculateProgress({
  location,
  draftAccount,
}: {
  draftAccount: DraftAccount;
  location: {
    pathname: string;
  };
}): number {
  const routeName = location.pathname;

  // Username screen only required for children
  const filteredRequirementScreens = draftAccount.useResponsibleEmail
    ? requirementScreens
    : (requirementScreens.filter(
        screen => screen !== routes.Username
      ) as string[]);

  const totalSteps =
    nonRequirementScreens.length + filteredRequirementScreens.length;
  // Which step are we on of each type of screen? "0" for N/A
  const [requirementsStep, nonRequirementsStep] = [
    filteredRequirementScreens,
    nonRequirementScreens,
  ].map(screens => screens.indexOf(routeName) + 1);

  if (routeName === routes.Success) {
    // Complete!
    return 1;
  }
  if (nonRequirementsStep !== 0) {
    // In pre-requirement screens
    return nonRequirementsStep / totalSteps;
  }
  if (requirementsStep !== 0) {
    // Past pre-requirement screens
    const successScreen = 1;
    return (
      (requirementsStep + (nonRequirementScreens.length - successScreen)) /
      totalSteps
    );
  }
  // This should never happen :)
  return 0;
}

export type ActionButtonProps = {
  id?: string;
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
};

export type PrimaryActionButtonProps = ActionButtonProps & {
  type: 'submit';
  color: 'primary';
};

export type SecondaryActionButtonProps = ActionButtonProps & {
  color: 'inherit';
  variant: 'outlined';
};

function actionButtonProps<T>(specificProps: T) {
  return (props: ActionButtonProps): ActionButtonProps & T => ({
    ...specificProps,
    ...props,
  });
}

export const primaryActionButtonProps = actionButtonProps<{
  type: 'submit';
  color: 'primary';
}>({
  type: 'submit',
  color: 'primary',
});

export const secondaryActionButtonProps = actionButtonProps<{
  color: 'inherit';
  variant: 'outlined';
}>({
  color: 'inherit',
  variant: 'outlined',
});
